import { Row } from "react-bootstrap";
import AttentionPoints from "../../../components/dashboard/attentionPoints";
import { StyledCardIndicators } from "../styles";
import { PageDataModel } from "../hooks/useInsightsData";
import { useTranslation } from 'react-i18next';

interface IndicatorCardsProps {
    pageData: PageDataModel;
    dateValue: string;
    representationSelectedOptions: string[];
    selectedGroupOptions: string[];
}

type HistoricEntry = {
    month?: string;
    year?: number | string;
    result?: number | null;
};

const monthNamesPt = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro"
];

const normalizeText = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const getMonthIndexFromName = (monthName?: string) => {
    if (!monthName) {
        return -1;
    }

    const normalizedMonth = normalizeText(monthName);
    const monthIndex = monthNamesPt.findIndex((name) => normalizeText(name) === normalizedMonth);
    return monthIndex >= 0 ? monthIndex : -1;
};

const getHistoricOrderValue = (entry: HistoricEntry) => {
    const year = typeof entry.year === "string" ? Number(entry.year) : entry.year;
    const monthIndex = getMonthIndexFromName(entry.month);

    if (typeof year !== "number" || Number.isNaN(year) || monthIndex < 0) {
        return Number.NaN;
    }

    return (year * 12) + monthIndex;
};

/**
 * Component to display the grid of indicator cards (AttentionPoints).
 */
const IndicatorCards = ({ pageData, dateValue, representationSelectedOptions, selectedGroupOptions }: IndicatorCardsProps) => {
    const { t } = useTranslation('insights');
    const hasFilters = representationSelectedOptions.length > 0 || selectedGroupOptions.length > 0;
    const filterTag = hasFilters ? t('indicatorFilter') : null;

    const getHistoricSource = (filteredHistoric: HistoricEntry[], historic: HistoricEntry[]) => (
        hasFilters ? filteredHistoric : historic
    );

    const getSortedValidHistoricValues = (historic: HistoricEntry[]) => (
        historic
            .filter((item) => typeof item?.result === "number" && !Number.isNaN(item.result))
            .slice()
            .sort((left, right) => getHistoricOrderValue(left) - getHistoricOrderValue(right))
    );

    const getHistoricVariation = (historic: HistoricEntry[]) => {
        const validHistoricValues = getSortedValidHistoricValues(historic);

        if (validHistoricValues.length < 2) {
            return null;
        }

        const oldestValidResult = validHistoricValues[0]?.result ?? null;
        const latestValidResult = validHistoricValues[validHistoricValues.length - 1]?.result ?? null;

        if (oldestValidResult === null || latestValidResult === null) {
            return null;
        }

        return latestValidResult - oldestValidResult;
    };

    const getDimensionLastResult = (historic: HistoricEntry[]) => {
        const validHistoricValues = getSortedValidHistoricValues(historic);
        return validHistoricValues[validHistoricValues.length - 1]?.result ?? null;
    };

    const getDimensionPointsDiff = (filteredHistoric: HistoricEntry[], historic: HistoricEntry[]) => (
        getHistoricVariation(getHistoricSource(filteredHistoric, historic))
    );

    return (
        <Row className='pt-4'>
            <StyledCardIndicators>
                <AttentionPoints
                    title={t('dimension.positive')}
                    date={dateValue}
                    pointsDiff={getDimensionPointsDiff(pageData.filteredEpHistoric, pageData.epHistoric)}
                    filterTag={filterTag}
                    dimensionLastResult={getDimensionLastResult(getHistoricSource(pageData.filteredEpHistoric, pageData.epHistoric))}
                />
                <AttentionPoints
                    title={t('dimension.engagement')}
                    date={dateValue}
                    pointsDiff={getDimensionPointsDiff(pageData.filteredEngHistoric, pageData.engHistoric)}
                    filterTag={filterTag}
                    dimensionLastResult={getDimensionLastResult(getHistoricSource(pageData.filteredEngHistoric, pageData.engHistoric))}
                />
                <AttentionPoints
                    title={t('dimension.relationship')}
                    date={dateValue}
                    pointsDiff={getDimensionPointsDiff(pageData.filteredRelHistoric, pageData.relHistoric)}
                    filterTag={filterTag}
                    dimensionLastResult={getDimensionLastResult(getHistoricSource(pageData.filteredRelHistoric, pageData.relHistoric))}
                />
                <AttentionPoints
                    title={t('dimension.meaning')}
                    date={dateValue}
                    pointsDiff={getDimensionPointsDiff(pageData.filteredSigHistoric, pageData.sigHistoric)}
                    filterTag={filterTag}
                    dimensionLastResult={getDimensionLastResult(getHistoricSource(pageData.filteredSigHistoric, pageData.sigHistoric))}
                />
                <AttentionPoints
                    title={t('dimension.accomplishment')}
                    date={dateValue}
                    pointsDiff={getDimensionPointsDiff(pageData.filteredReaHistoric, pageData.reaHistoric)}
                    filterTag={filterTag}
                    dimensionLastResult={getDimensionLastResult(getHistoricSource(pageData.filteredReaHistoric, pageData.reaHistoric))}
                />
                <AttentionPoints
                    title={t('dimension.vitality')}
                    date={dateValue}
                    pointsDiff={getDimensionPointsDiff(pageData.filteredVitHistoric, pageData.vitHistoric)}
                    filterTag={filterTag}
                    dimensionLastResult={getDimensionLastResult(getHistoricSource(pageData.filteredVitHistoric, pageData.vitHistoric))}
                />
            </StyledCardIndicators>
        </Row>
    );
};

export default IndicatorCards;
